import {
  Box, Card, CardContent, Typography, Button, TextField, Table, TableBody,
  TableCell, TableHead, TableRow, Chip, Pagination, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, MenuItem,
  IconButton, Divider, Alert,
} from '@mui/material';
import { Add, Delete, Undo, Print } from '@mui/icons-material';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';
import api from '../../api/axios';
import { showSnackbar } from '../ui/uiSlice';
import PurchaseGRN from './PurchaseGRN';

const statusColor = { paid: 'success', partial: 'warning', due: 'error' };

function PurchaseReturnDialog({ purchaseId, open, onClose, onSuccess, dispatch }) {
  const [purchase, setPurchase] = useState(null);
  const [qtys, setQtys] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !purchaseId) return;
    setLoading(true); setError(''); setQtys({});
    api.get(`/purchases/${purchaseId}`).then(r => {
      setPurchase(r.data.data);
      const init = {};
      r.data.data.items?.forEach(i => { init[i.id] = 0; });
      setQtys(init);
    }).catch(() => setError('Failed to load purchase')).finally(() => setLoading(false));
  }, [open, purchaseId]);

  const handleSubmit = async () => {
    const items = Object.entries(qtys).filter(([, q]) => q > 0).map(([id, qty]) => ({ purchase_item_id: +id, return_quantity: +qty }));
    if (!items.length) { setError('Select at least one item to return'); return; }
    setSubmitting(true); setError('');
    try {
      await api.post(`/purchases/${purchaseId}/return`, { items, reason: 'Customer return' });
      dispatch(showSnackbar({ message: 'Return processed', severity: 'success' }));
      onSuccess();
    } catch (e) { setError(e.response?.data?.message || 'Return failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Purchase Return #{purchaseId}</DialogTitle>
      <DialogContent>
        {loading && <CircularProgress />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {purchase && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell align="right">Purchased</TableCell>
                <TableCell align="right">Return Qty</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {purchase.items?.map(item => (
                <TableRow key={item.id}>
                  <TableCell>{item.product_name}</TableCell>
                  <TableCell align="right">{item.quantity}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                      <IconButton size="small" onClick={() => setQtys(q => ({ ...q, [item.id]: Math.max(0, (q[item.id] || 0) - 1) }))}>−</IconButton>
                      <Typography sx={{ minWidth: 24, textAlign: 'center' }}>{qtys[item.id] || 0}</Typography>
                      <IconButton size="small" onClick={() => setQtys(q => ({ ...q, [item.id]: Math.min(item.quantity, (q[item.id] || 0) + 1) }))}>+</IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="warning" onClick={handleSubmit} disabled={submitting} startIcon={<Undo />}>Process Return</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function PurchasesPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [purchases, setPurchases] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [returnPurchaseId, setReturnPurchaseId] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [grnPurchase, setGrnPurchase] = useState(null);
  const [tenant, setTenant] = useState(null);
  const grnRef = useRef();
  const handlePrintGRN = useReactToPrint({ contentRef: grnRef });

  const openGRN = async (id) => {
    try {
      const [pRes, tRes] = await Promise.all([
        api.get(`/purchases/${id}`),
        api.get('/settings/business'),
      ]);
      setGrnPurchase(pRes.data.data);
      setTenant(tRes.data.data);
      setTimeout(handlePrintGRN, 300);
    } catch {
      dispatch(showSnackbar({ message: 'Failed to load purchase', severity: 'error' }));
    }
  };

  const { register, handleSubmit, control, watch, reset, setValue } = useForm({
    defaultValues: { items: [{ product_id: '', product_name: '', quantity: 1, unit_price: 0, batch_number: '', expiry_date: '' }] },
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'items' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/purchases', { params: { page, limit: 15 } });
      setPurchases(data.data); setTotal(data.pagination.total);
    } finally { setLoading(false); }
  }, [page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/suppliers?limit=100').then(r => setSuppliers(r.data.data));
    api.get('/products?limit=200').then(r => setProducts(r.data.data));
  }, []);

  const items = watch('items');
  const subtotal = items.reduce((s, i) => s + (i.unit_price || 0) * (i.quantity || 0), 0);

  const onProductChange = (idx, productId) => {
    const product = products.find(p => p.id === +productId);
    if (product) setValue(`items.${idx}.product_name`, product.name);
  };

  const onSubmit = async (data) => {
    try {
      await api.post('/purchases', data);
      dispatch(showSnackbar({ message: 'Purchase recorded', severity: 'success' }));
      setDialogOpen(false); reset(); load();
    } catch (e) {
      dispatch(showSnackbar({ message: e.response?.data?.message || 'Error', severity: 'error' }));
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>{t('nav.purchases')}</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => { reset(); setDialogOpen(true); }}>New Purchase</Button>
      </Box>

      <Card>
        <CardContent>
          {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Ref #</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Due</TableCell>
                  <TableCell>Payment</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchases.map(p => (
                  <TableRow key={p.id} hover>
                    <TableCell><Typography variant="body2" fontWeight={600} color="primary">#{p.id}</Typography></TableCell>
                    <TableCell>{p.supplier_name || 'Walk-in'}</TableCell>
                    <TableCell align="right">₨ {p.total_amount}</TableCell>
                    <TableCell align="right">₨ {p.paid_amount}</TableCell>
                    <TableCell align="right" sx={{ color: p.due_amount > 0 ? 'error.main' : 'inherit' }}>₨ {p.due_amount}</TableCell>
                    <TableCell><Chip label={p.payment_status} size="small" color={statusColor[p.payment_status] || 'default'} /></TableCell>
                    <TableCell><Typography variant="caption">{new Date(p.created_at).toLocaleDateString('en-PK')}</Typography></TableCell>
                    <TableCell>
                      <IconButton size="small" color="primary" title="Print GRN" onClick={() => openGRN(p.id)}><Print fontSize="small" /></IconButton>
                      <IconButton size="small" color="warning" title="Return" onClick={() => setReturnPurchaseId(p.id)}><Undo fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination count={Math.ceil(total / 15)} page={page} onChange={(_, v) => setPage(v)} color="primary" />
          </Box>
        </CardContent>
      </Card>

      {/* Hidden GRN print area */}
      <Box sx={{ display: 'none' }}>
        <PurchaseGRN ref={grnRef} purchase={grnPurchase} tenant={tenant} />
      </Box>

      <PurchaseReturnDialog
        purchaseId={returnPurchaseId}
        open={!!returnPurchaseId}
        onClose={() => setReturnPurchaseId(null)}
        onSuccess={() => { setReturnPurchaseId(null); load(); }}
        dispatch={dispatch}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Purchase</DialogTitle>
        <DialogContent>
          <Box component="form" id="purchase-form" onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth select label="Supplier (Optional)" defaultValue="" {...register('supplier_id')}>
                  <MenuItem value="">— No Supplier —</MenuItem>
                  {suppliers.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Notes" {...register('notes')} />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" fontWeight={600} mb={1}>Items</Typography>

            {fields.map((field, idx) => (
              <Grid container spacing={1.5} key={field.id} sx={{ mb: 1.5, alignItems: 'flex-end' }}>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth select size="small" label="Product" defaultValue="" {...register(`items.${idx}.product_id`)}
                    onChange={e => onProductChange(idx, e.target.value)}>
                    {products.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={4} md={1.5}>
                  <TextField fullWidth size="small" label="Qty" type="number" {...register(`items.${idx}.quantity`, { min: 1 })} />
                </Grid>
                <Grid item xs={4} md={2}>
                  <TextField fullWidth size="small" label="Unit Price" type="number" {...register(`items.${idx}.unit_price`, { min: 0 })} />
                </Grid>
                <Grid item xs={4} md={2}>
                  <TextField fullWidth size="small" label="Batch #" {...register(`items.${idx}.batch_number`)} />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField fullWidth size="small" label="Expiry" type="date" InputLabelProps={{ shrink: true }} {...register(`items.${idx}.expiry_date`)} />
                </Grid>
                <Grid item xs={6} md={0.5}>
                  {fields.length > 1 && <IconButton color="error" onClick={() => remove(idx)}><Delete fontSize="small" /></IconButton>}
                </Grid>
              </Grid>
            ))}

            <Button startIcon={<Add />} onClick={() => append({ product_id: '', product_name: '', quantity: 1, unit_price: 0, batch_number: '', expiry_date: '' })}>
              Add Item
            </Button>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Amount Paid (₨)" type="number" {...register('paid_amount')} helperText={`Total: ₨ ${subtotal.toFixed(0)}`} />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" type="submit" form="purchase-form">Save Purchase</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
